/**
 * CRM Task API Service
 * Phase 7 - Handles all CRM task API calls
 */

import { apiClient } from './aiCrmApi';

// Types
export interface CRMTask {
  id: string;
  taskNo: string;
  title: string;
  description?: string;
  taskType: 'CALL' | 'EMAIL' | 'MEETING' | 'FOLLOW_UP' | 'QUOTE' | 'DEMO' | 'OTHER';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'OVERDUE';
  leadId?: string;
  customerId?: string;
  salesOrderId?: string;
  assignedToUserId: string;
  dueDate: string;
  reminderDate?: string;
  completedAt?: string;
  isAIGenerated: boolean;
  aiSuggestion?: string;
  aiPriority?: number;
  outcome?: 'SUCCESSFUL' | 'UNSUCCESSFUL' | 'RESCHEDULED';
  notes?: string;
  createdBy?: string;
  companyId: string;
  createdAt: string;
  updatedAt: string;
  // Relations
  lead?: {
    id: string;
    leadNo: string;
    contactName: string;
    companyName?: string;
  };
  customer?: {
    id: string;
    name: string;
  };
  assignedTo: {
    id: string;
    name: string;
    email: string;
  };
}

export interface TaskFilters {
  status?: string;
  taskType?: string;
  priority?: string;
  leadId?: string;
  customerId?: string;
  assignedToUserId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface TaskSummary {
  byStatus: {
    pending: number;
    inProgress: number;
    completed: number;
    cancelled: number;
    overdue: number;
  };
  byPriority: {
    low: number;
    medium: number;
    high: number;
    urgent: number;
  };
  dueToday: number;
  dueThisWeek: number;
  total: number;
  completionRate: number;
}

export interface CreateTaskInput {
  title: string;
  description?: string;
  taskType: string;
  priority?: string;
  leadId?: string;
  customerId?: string;
  salesOrderId?: string;
  assignedToUserId: string;
  dueDate: string;
  reminderDate?: string;
  isAIGenerated?: boolean;
  aiSuggestion?: string;
  aiPriority?: number;
}

export interface UpdateTaskInput {
  title?: string;
  description?: string;
  taskType?: string;
  priority?: string;
  status?: string;
  leadId?: string;
  customerId?: string;
  salesOrderId?: string;
  assignedToUserId?: string;
  dueDate?: string;
  reminderDate?: string;
  outcome?: string;
  notes?: string;
}

export interface TaskAssignee {
  id: string;
  name: string;
  email: string;
  role: string;
}

// Lead Assignment Types
export interface UnassignedLead {
  id: string;
  leadNo: string;
  contactName: string;
  companyName?: string;
  email?: string;
  phone?: string;
  status: string;
  priority: string;
  source: string;
  industry?: string;
  aiScore?: number;
  potentialValue?: number;
  createdAt: string;
}

export interface AssignedLead extends UnassignedLead {
  assignedTo: {
    id: string;
    name: string;
    email: string;
  };
}

export interface LeadAssignmentSummary {
  unassignedCount: number;
  assignedCount: number;
  totalLeads: number;
  staffSummary: {
    id: string;
    name: string;
    email: string;
    role: string;
    leadCount: number;
  }[];
}

export interface LeadAssignmentFilters {
  status?: string;
  priority?: string;
  assignedToUserId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

// Task API
export const taskApi = {
  /**
   * Get all tasks with optional filters
   */
  getTasks: async (filters?: TaskFilters): Promise<PaginatedResponse<CRMTask>> => {
    const response = await apiClient.get<PaginatedResponse<CRMTask>>('/tasks', {
      params: filters
    });
    return response.data;
  },

  /**
   * Get task summary statistics
   */
  getTasksSummary: async (): Promise<TaskSummary> => {
    const response = await apiClient.get<ApiResponse<TaskSummary>>('/tasks/summary');
    return response.data.data;
  },

  /**
   * Get task by ID
   */
  getTaskById: async (id: string): Promise<CRMTask> => {
    const response = await apiClient.get<ApiResponse<CRMTask>>(`/tasks/${id}`);
    return response.data.data;
  },

  /**
   * Create a new task
   */
  createTask: async (data: CreateTaskInput): Promise<CRMTask> => {
    const response = await apiClient.post<ApiResponse<CRMTask>>('/tasks', data);
    return response.data.data;
  },

  /**
   * Update a task
   */
  updateTask: async (id: string, data: UpdateTaskInput): Promise<CRMTask> => {
    const response = await apiClient.put<ApiResponse<CRMTask>>(`/tasks/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete a task
   */
  deleteTask: async (id: string): Promise<void> => {
    await apiClient.delete(`/tasks/${id}`);
  },

  /**
   * Update task status
   */
  updateTaskStatus: async (id: string, status: string, outcome?: string, notes?: string): Promise<CRMTask> => {
    const response = await apiClient.put<ApiResponse<CRMTask>>(`/tasks/${id}/status`, {
      status,
      outcome,
      notes
    });
    return response.data.data;
  },

  /**
   * Get tasks for calendar view
   */
  getTasksCalendar: async (startDate: string, endDate: string, assignedToUserId?: string): Promise<CRMTask[]> => {
    const response = await apiClient.get<ApiResponse<CRMTask[]>>('/tasks/calendar', {
      params: { startDate, endDate, assignedToUserId }
    });
    return response.data.data;
  },

  /**
   * Get available task assignees
   */
  getTaskAssignees: async (): Promise<TaskAssignee[]> => {
    const response = await apiClient.get<ApiResponse<TaskAssignee[]>>('/tasks/assignees');
    return response.data.data;
  },

  /**
   * Bulk update task status
   */
  bulkUpdateTaskStatus: async (taskIds: string[], status: string): Promise<{ count: number }> => {
    const response = await apiClient.post<ApiResponse<{ count: number }>>('/tasks/bulk-update-status', {
      taskIds,
      status
    });
    return response.data.data;
  },

  // ===== LEAD ASSIGNMENT =====

  /**
   * Get unassigned leads
   */
  getUnassignedLeads: async (filters?: LeadAssignmentFilters): Promise<PaginatedResponse<UnassignedLead>> => {
    const response = await apiClient.get<PaginatedResponse<UnassignedLead>>('/tasks/leads/unassigned', {
      params: filters
    });
    return response.data;
  },

  /**
   * Get assigned leads
   */
  getAssignedLeads: async (filters?: LeadAssignmentFilters): Promise<PaginatedResponse<AssignedLead>> => {
    const response = await apiClient.get<PaginatedResponse<AssignedLead>>('/tasks/leads/assigned', {
      params: filters
    });
    return response.data;
  },

  /**
   * Get lead assignment summary
   */
  getLeadAssignmentSummary: async (): Promise<LeadAssignmentSummary> => {
    const response = await apiClient.get<ApiResponse<LeadAssignmentSummary>>('/tasks/leads/assignment-summary');
    return response.data.data;
  },

  /**
   * Assign a lead to a staff member
   */
  assignLead: async (leadId: string, assignedToUserId: string): Promise<AssignedLead> => {
    const response = await apiClient.post<ApiResponse<AssignedLead>>('/tasks/leads/assign', {
      leadId,
      assignedToUserId
    });
    return response.data.data;
  },

  /**
   * Bulk assign leads to a staff member
   */
  bulkAssignLeads: async (leadIds: string[], assignedToUserId: string): Promise<{ count: number }> => {
    const response = await apiClient.post<ApiResponse<{ count: number }>>('/tasks/leads/bulk-assign', {
      leadIds,
      assignedToUserId
    });
    return response.data.data;
  }
};

export default taskApi;
